/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type * as util from '../core/util'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// Date schema definition
export interface DateSchemaDef extends types.SchemaTypeDef {
  type: 'date' | 'timestamp'
}

export interface DateSchemaInternals extends types.SchemaInternals<
  Date,
  Date
> {
  def: DateSchemaDef
  bag: util.LoosePartial<{
    minimum: Date
    maximum: Date
  }>
}

export interface DateSchema extends types.Schema<
  Date,
  Date,
  DateSchemaInternals
> {
  min(date: Date, message?: string | { message: string | (() => string) }): this
  max(date: Date, message?: string | { message: string | (() => string) }): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: Date | (() => Date)): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): Date
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: Date }
    | { success: false; error: errors.SchemaError }
}

export const DateSchema = types.$constructor<DateSchema>(
  'DateSchema',
  (inst, def: DateSchemaDef) => {
    BaseSchema.init(inst, def)

    inst._zod.parse = (payload, _ctx) => {
      let date: Date

      if (payload.value instanceof Date) date = payload.value
      else {
        payload.issues.push({ code: 'invalid_type', expected: 'date', input: payload.value, inst })
        return payload
      }

      // Check if date is valid
      if (Number.isNaN(date.getTime())) {
        payload.issues.push({
          code: 'invalid_type',
          expected: 'date',
          received: 'Invalid Date',
          input: payload.value,
          inst,
        })
        return payload
      }

      payload.value = date
      return payload
    }

    const createCheck = (
      checkFn: types.CheckFn<Date>,
      onattach: (schema: any) => void = () => {},
    ) => ({
      _zod: {
        def: { check: 'custom' },
        check: checkFn,
        onattach: [onattach],
      },
    })

    const addCheck = (check: types.Check<Date>) => {
      return new DateSchema({
        ...def,
        checks: [...(def.checks ?? []), check],
      })
    }

    inst.min = (date, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value.getTime() >= date.getTime()) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
            continue: true,
              type: 'date',
              minimum: date.getTime(),
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.minimum
            if (!curr || date.getTime() > curr.getTime()) {
              schema._zod.bag.minimum = date
            }
          },
        ),
      )
    }

    inst.max = (date, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value.getTime() <= date.getTime()) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_big',
            continue: true,
              type: 'date',
              maximum: date.getTime(),
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.maximum
            if (!curr || date.getTime() < curr.getTime()) {
              schema._zod.bag.maximum = date
            }
          },
        ),
      )
    }

    inst.default = (defaultValue: Date | (() => Date)) => {
      return makeDefault(inst, defaultValue)
    }
  },
)

export function date(
  message?: string | { message: string | (() => string) },
): DateSchema {
  return new DateSchema({
    type: 'date',
    ...(message
      ? { error: typeof message === 'string' ? message : message.message }
      : {}),
  })
}

export function timestamp(): DateSchema {
  return new DateSchema({
    type: 'timestamp',
    proto: {
      scalar: { type: 'timestamp', wireType: 'length-delimited' },
    },
  })
}
